import React, { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { MagnifyingGlass, Clock, MapPin, X, Warning } from "@phosphor-icons/react";

/**
 * LocationPicker — selector universal de ubicación.
 * - Autocompleta sobre estructura (habs/zonas/edificios) + activos del tenant.
 * - Muestra últimas 6 ubicaciones usadas por el usuario.
 * - Si no hay match, permite texto libre marcado "sin catalogar".
 * value: { value, kind, label, asset_id?, uncatalogued: bool } | null
 */
export default function LocationPicker({ value, onChange }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [recent, setRecent] = useState([]);
  const [showList, setShowList] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/locations/search?q=").then(r => setRecent(r.data.recent || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setResults([]); return; }
    timer.current = setTimeout(() => {
      api.get(`/locations/search?q=${encodeURIComponent(q)}`)
         .then(r => setResults(r.data.results || []))
         .catch(() => setResults([]));
    }, 180);
    return () => clearTimeout(timer.current);
  }, [q]);

  const pick = (loc) => {
    onChange(loc);
    setQ(loc.label);
    setShowList(false);
    api.post("/locations/recent", loc).catch(() => {});
  };

  const useAsFreeText = () => {
    const raw = q.trim();
    if (!raw) return;
    const loc = { label: raw, value: raw, kind: "libre", uncatalogued: true };
    onChange(loc);
    setShowList(false);
  };

  const clear = () => { onChange(null); setQ(""); setShowList(true); };

  const noMatch = q.trim().length >= 1 && results.length === 0;

  return (
    <div className="relative">
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[color:var(--q-muted)]"/>
        <input
          data-testid="location-search"
          value={q}
          onChange={(e) => { setQ(e.target.value); setShowList(true); if (value) onChange(null); }}
          onFocus={() => setShowList(true)}
          placeholder="Buscar hab., zona o activo (ej: 119, piscina, bomba)"
          className="w-full h-12 pl-10 pr-10 rounded-lg border border-[color:var(--q-border)] bg-white focus:outline-none focus:ring-2 focus:ring-[color:var(--q-navy)]"
        />
        {value && (
          <button type="button" onClick={clear} data-testid="location-clear" className="absolute right-3 top-1/2 -translate-y-1/2 text-[color:var(--q-muted)] hover:text-[color:var(--q-navy)]">
            <X size={16}/>
          </button>
        )}
      </div>

      {value && (
        <div className="mt-2 p-3 rounded-lg border border-[color:var(--q-navy)]/40 bg-[color:var(--q-navy)]/5 flex items-center gap-2" data-testid="location-selected">
          <MapPin size={16} weight="fill" className="text-[color:var(--q-navy)]"/>
          <span className="text-sm font-semibold">{value.label}</span>
          {value.uncatalogued && (
            <span className="chip chip-media text-[10px] ml-auto" data-testid="uncatalogued-badge">
              <Warning size={10} className="inline mr-0.5"/> SIN CATALOGAR
            </span>
          )}
        </div>
      )}

      {showList && !value && (
        <div className="absolute z-30 left-0 right-0 mt-2 q-card p-2 max-h-80 overflow-y-auto" onMouseDown={(e) => e.preventDefault()}>
          {q.trim() === "" && recent.length > 0 && (
            <>
              <div className="q-label text-[color:var(--q-muted)] px-2 py-1 flex items-center gap-1"><Clock size={11}/> Recientes</div>
              {recent.map((r, i) => (
                <button key={i} type="button" onClick={() => pick(r)} data-testid={`recent-${i}`}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-50 rounded flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-[color:var(--q-muted)]"/>
                  <span>{r.label}</span>
                  <span className="ml-auto q-label text-[color:var(--q-muted)]">{r.kind}</span>
                </button>
              ))}
            </>
          )}
          {q.trim() !== "" && results.length > 0 && (
            <>
              <div className="q-label text-[color:var(--q-muted)] px-2 py-1">Coincidencias</div>
              {results.map((r, i) => (
                <button key={i} type="button" onClick={() => pick(r)} data-testid={`match-${i}`}
                        className="w-full text-left px-3 py-2 hover:bg-neutral-50 rounded flex items-center gap-2 text-sm">
                  <MapPin size={14} className="text-[color:var(--q-navy)]"/>
                  <span className="font-medium">{r.label}</span>
                  <span className="ml-auto q-label text-[color:var(--q-muted)]">{r.kind}</span>
                </button>
              ))}
            </>
          )}
          {noMatch && (
            <button type="button" onClick={useAsFreeText} data-testid="use-freetext"
                    className="w-full text-left px-3 py-2 hover:bg-amber-50 rounded flex items-center gap-2 text-sm border-2 border-dashed border-amber-300 mt-1">
              <Warning size={14} className="text-amber-600"/>
              <span>Usar "<span className="font-semibold">{q.trim()}</span>" como ubicación sin catalogar</span>
            </button>
          )}
          {q.trim() === "" && recent.length === 0 && (
            <div className="p-4 text-xs text-[color:var(--q-muted)] text-center">Escribe para buscar habitaciones, zonas o activos.</div>
          )}
        </div>
      )}
    </div>
  );
}
